import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { writeToClipboard } from "../../lib/writeToClipboard";
import type { App } from "./Form";

type AuthorizationRequest =
  | { action: "start"; attemptId: string; email: string }
  | { action: "complete"; attemptId: string };

export function useConnectorAuthorization(app: App, disabled?: boolean) {
  const z = useZero();
  const copyAttempt = useRef<string | null>(null);
  const requestSequence = useRef(0);
  const [interruptedAttempt, setInterruptedAttempt] = useState<string | null>(
    null
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingAttempt, setWaitingAttempt] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const {
    connectorAuthorizationId: attemptId,
    connectorAuthorizationCode: code,
    connectorAuthorizationStatus: status,
    connectorAuthorizationNextCheckAt: nextCheckAt,
  } = app.configuration;
  const interrupted =
    interruptedAttempt === attemptId &&
    (status === "registering" || status === "checking");
  const polling =
    !interrupted &&
    waitingAttempt === attemptId &&
    (status === "awaitingApproval" || status === "checking");

  useEffect(() => {
    if (!code || copyAttempt.current !== attemptId) return;
    copyAttempt.current = null;
    let cancelled = false;
    void writeToClipboard(code).then((copied) => {
      if (copied && !cancelled) setCopiedCode(code);
    });
    return () => {
      cancelled = true;
    };
  }, [code, attemptId]);

  useEffect(() => {
    if (status !== "registering" && status !== "checking") return;
    const timer = setTimeout(() => {
      requestSequence.current++;
      setInterruptedAttempt(attemptId!);
      setWaitingAttempt(null);
      setPending(false);
    }, 30_000);
    return () => clearTimeout(timer);
  }, [status, attemptId]);

  const submit = async (request: AuthorizationRequest) => {
    const sequence = ++requestSequence.current;
    setPending(true);
    setError(null);
    try {
      const result = await z.mutate(
        request.action === "start"
          ? sharedMutators.startConnectorAuthorization({
              connectedAppId: app.id,
              attemptId: request.attemptId,
              email: request.email,
            })
          : sharedMutators.completeConnectorAuthorization({
              connectedAppId: app.id,
              attemptId: request.attemptId,
            })
      ).server;
      if (sequence !== requestSequence.current) return;
      if (result.type === "error") {
        setError("Couldn’t update the authorization. Try again.");
        setWaitingAttempt(null);
      }
    } catch {
      if (sequence !== requestSequence.current) return;
      setError("Couldn’t reach 3B. Check your connection and try again.");
      setWaitingAttempt(null);
    }
    setPending(false);
  };

  const checkApproval = useEffectEvent(() => {
    if (attemptId) return submit({ action: "complete", attemptId });
  });
  useEffect(() => {
    if (!polling || disabled || pending || status !== "awaitingApproval")
      return;
    const delay = Math.max(
      1000,
      nextCheckAt ? Number(nextCheckAt) - Date.now() : 5000
    );
    const timer = setTimeout(() => {
      void checkApproval();
    }, delay);
    return () => clearTimeout(timer);
  }, [polling, disabled, pending, status, nextCheckAt, attemptId]);

  return {
    pending,
    error: interrupted
      ? "Authorization was interrupted. Generate a new code to try again."
      : error,
    interrupted,
    polling,
    copied: Boolean(code && copiedCode === code),
    start: (email: string) => {
      setWaitingAttempt(null);
      const newAttemptId = nanoid(12);
      copyAttempt.current = newAttemptId;
      setInterruptedAttempt(null);
      return submit({
        action: "start",
        email: email.trim(),
        attemptId: newAttemptId,
      });
    },
    openVerification: () => {
      setError(null);
      setWaitingAttempt(attemptId!);
      if (code)
        void writeToClipboard(code).then((copied) => {
          if (copied) setCopiedCode(code);
        });
    },
  };
}
